/**
 * Property Gallery Runtime
 * Hydrates gallery with lightbox functionality using jQuery
 */

import $ from 'jquery';
import { buildEdgeNav, enableDrag, navArrowMarkup, releaseVerticalWheel } from '../../_shared/carousel';

// Whether to print the Hostaway caption under each photo, decided per property
// by the `show_gallery_captions` field and published by render.php on the block
// root -- most properties' captions are just the source filename, but that's an
// editorial call rather than something guessable from the text.

$(() => {
	// Floor plans reuse this same lightbox rather than carrying a second
	// implementation -- they live in the property-content block, but the
	// viewer belongs here, so the binding does too. Their own group, so the
	// plans open as a set of plans rather than joining the photo gallery.
	const $floorPlanZooms = $('[data-lightbox="property-floorplans"]');

	$floorPlanZooms.on('click', function (e) {
		e.preventDefault();

		const images = $floorPlanZooms.toArray().map((item) => ({
			url: $(item).attr('href'),
			title: $(item).data('title') || '',
			caption: $(item).data('title') || '',
		}));

		// Captions here are the alt text render.php already resolved -- it
		// falls back to "Floor plan N of M for <property>" when the Hostaway
		// caption isn't trusted, so it is always something worth showing.
		openLightbox(images, $floorPlanZooms.index(this), true);
	});

	const $galleries = $('.property-gallery');

	if (!$galleries.length) {
		return;
	}

	$galleries.each((index, gallery) => {
		const $gallery = $(gallery);
		const $items = $gallery.find('.property-gallery__item a');

		// Initialize lightbox on gallery items
		$items.on('click', function (e) {
			e.preventDefault();
			
			const $clicked = $(this);
			const images = $items.toArray().map((item) => ({
				url: $(item).attr('href'),
				title: $(item).data('title') || '',
				caption: $(item).data('title') || '',
			}));
			
			const clickedIndex = $items.index($clicked);

			// Read per gallery rather than once at module scope: a page could
			// hold more than one property's gallery (a preview screen, say),
			// and the flag belongs to the property, not the page.
			const showCaptions = $gallery.data('show-captions') !== 0;

			// Create lightbox modal
			openLightbox(images, clickedIndex, showCaptions);
		});
	});

	function openLightbox(images: Array<{url: string; title: string; caption: string}>, startIndex: number, showCaptions: boolean) {
		let currentIndex = startIndex;

		// Temporary debug aid -- lists every image the gallery is passing to
		// the lightbox and flags any repeated URL, to help track down
		// reported duplicate images per property.
		console.groupCollapsed(`[property-gallery] ${images.length} image(s)`);
		const seenUrls = new Map<string, number[]>();
		images.forEach((image, index) =>
			{
			console.log(index, image.caption, image.url);
			seenUrls.set(image.url, [...(seenUrls.get(image.url) ?? []), index]);
			});
		seenUrls.forEach((indices, url) =>
			{
			if (indices.length > 1)
				{
				console.warn(`[property-gallery] duplicate URL at indices ${indices.join(', ')}:`, url);
				}
			});
		console.groupEnd();

		// Create modal HTML
		//
		// The prev/next pair are the theme's standard .ocean-nav-button (see
		// scss/_nav-button.scss), same control as every carousel and calendar
		// arrow, rather than the `<` / `>` text glyphs this used to draw. Sized
		// up via .is-style-large and overlaid on the image's left/right edges by
		// the stylesheet.
		// The glyph is a complete <svg> that already carries the
		// .ocean-nav-button-icon class, so it goes in unwrapped -- a <span>
		// around it would apply that class's scale transform a second time.
		const icon = navArrowMarkup();
		const navButton = (direction: 'prev' | 'next') =>
			`<button type="button" ` +
				`class="property-gallery-lightbox__${direction} ocean-nav-button ocean-nav-button--${direction} is-style-large" ` +
				`aria-label="${direction === 'prev' ? 'Previous image' : 'Next image'}">` +
				icon +
			`</button>`;

		const $modal = $(`
			<div class="property-gallery-lightbox" role="dialog" aria-modal="true" aria-label="Property photos">
				<div class="property-gallery-lightbox__overlay"></div>
				<div class="property-gallery-lightbox__content">
					<button type="button" class="property-gallery-lightbox__close" aria-label="Close gallery">
						<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
							<path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
						</svg>
					</button>
					<div class="property-gallery-lightbox__row">
						${navButton('prev')}
						<div class="property-gallery-lightbox__image-container">
							<img class="property-gallery-lightbox__image" src="" alt="">
						</div>
						${navButton('next')}
					</div>
					<div class="property-gallery-lightbox__meta">
						<div class="property-gallery-lightbox__caption"></div>
						<div class="property-gallery-lightbox__counter"></div>
					</div>
					<div class="property-gallery-lightbox__thumbs">
						<div class="property-gallery-lightbox__thumbs-track"></div>
					</div>
				</div>
			</div>
		`);

		// A strip of one thumbnail is just a smaller copy of the photo already
		// on screen, and the prev/next arrows have nothing to move between --
		// so a single-image lightbox drops both and shows the photo alone.
		if (images.length < 2)
			{
			$modal.find('.property-gallery-lightbox__thumbs').remove();
			$modal.find('.property-gallery-lightbox__prev, .property-gallery-lightbox__next').remove();
			}

		const $thumbs = $modal.find('.property-gallery-lightbox__thumbs-track');
		images.forEach((image, index) =>
			{
			const $thumb = $(`<button type="button" class="property-gallery-lightbox__thumb" aria-label="Show image ${index + 1}"><img src="${image.url}" alt="" loading="lazy"></button>`);
			$thumb.on('click', (e) =>
				{
				e.stopPropagation();
				showImage(index);
				});
			$thumbs.append($thumb);
			});

		// Append to body
		$('body').append($modal);

		// Give the thumbnail strip the same behaviour as every other track in
		// the theme, using the shared runtime's own pieces rather than a second
		// implementation: pointer drag with the snap-back settle, the vertical
		// wheel release (so a trackpad swipe over the strip scrolls the page
		// rather than latching onto the strip), and the hover-revealed edge
		// arrows. initCarousel() itself can't be used here -- it sweeps the DOM
		// at document-ready, and this strip is built on open, long after.
		const thumbsTrack = $thumbs.get(0) as HTMLElement;

		// Rest points are each thumb's own offset, clamped to the end of the
		// track -- the same "nearest sensible stop" idea initCarousel uses, kept
		// simple here because every thumb is the same width.
		const thumbRestPoints = () =>
			{
			const maxScroll = Math.max(0, thumbsTrack.scrollWidth - thumbsTrack.clientWidth);
			const points: number[] = [];
			$thumbs.children().each((_i, thumb) =>
				{
				const point = Math.min((thumb as HTMLElement).offsetLeft - thumbsTrack.offsetLeft, maxScroll);
				if (!points.length || point - points[points.length - 1] > 1)
					points.push(point);
				});
			if (!points.length || points[points.length - 1] < maxScroll - 1)
				points.push(maxScroll);
			return points;
			};

		const nearestThumbPoint = () =>
			thumbRestPoints().reduce((best, point) =>
				Math.abs(point - thumbsTrack.scrollLeft) < Math.abs(best - thumbsTrack.scrollLeft) ? point : best);

		enableDrag(thumbsTrack, () =>
			thumbsTrack.scrollTo({ left: nearestThumbPoint(), behavior: 'smooth' }));
		releaseVerticalWheel(thumbsTrack);

		// The floating white round arrows, overlaid on the strip's edges.
		const $thumbNav = buildEdgeNav($thumbs);
		const $thumbPrev = $thumbNav.filter('.ocean-nav-edge__button--prev');
		const $thumbNext = $thumbNav.filter('.ocean-nav-edge__button--next');

		const updateThumbNav = () =>
			{
			const points = thumbRestPoints();
			$thumbPrev.prop('disabled', thumbsTrack.scrollLeft <= points[0] + 1);
			$thumbNext.prop('disabled', thumbsTrack.scrollLeft >= points[points.length - 1] - 1);
			};

		$thumbPrev.on('click', () =>
			{
			const points = thumbRestPoints();
			const target = [...points].reverse().find(point => point < thumbsTrack.scrollLeft - 1);
			thumbsTrack.scrollTo({ left: target ?? points[0], behavior: 'smooth' });
			});
		$thumbNext.on('click', () =>
			{
			const points = thumbRestPoints();
			const target = points.find(point => point > thumbsTrack.scrollLeft + 1);
			thumbsTrack.scrollTo({ left: target ?? points[points.length - 1], behavior: 'smooth' });
			});

		$thumbs.on('scroll', updateThumbNav);
		updateThumbNav();

		// The main image is a track too, as far as dragging goes: a horizontal
		// drag across the photo pages it, matching how the thumbnail strip and
		// every card slider behave. It isn't a scroll container, so this reads
		// the gesture off the row and turns it into a paging step rather than
		// handing it to enableDrag.
		const $row = $modal.find('.property-gallery-lightbox__row');
		let imageDragX: number | null = null;
		let imageDragged = false;

		$row.on('pointerdown', (e) =>
			{
			const ev = e.originalEvent as PointerEvent;
			if (ev.pointerType === 'touch' || ev.button !== 0)
				return;
			imageDragX = ev.clientX;
			imageDragged = false;
			});

		$row.on('pointermove', (e) =>
			{
			if (imageDragX === null)
				return;
			const delta = (e.originalEvent as PointerEvent).clientX - imageDragX;
			// One page per gesture: past the threshold the slide changes and the
			// drag is spent, so a long sweep doesn't run through the whole set.
			if (Math.abs(delta) < 60 || imageDragged)
				return;
			imageDragged = true;
			if (delta < 0 && currentIndex < images.length - 1)
				showImage(currentIndex + 1);
			else if (delta > 0 && currentIndex > 0)
				showImage(currentIndex - 1);
			});

		const endImageDrag = () => { imageDragX = null; };
		$row.on('pointerup pointercancel pointerleave', endImageDrag);

		// Images are natively draggable and a started HTML5 drag cancels the
		// pointer stream -- same guard enableDrag() applies to its own tracks.
		$row.on('dragstart', (e) => { e.preventDefault(); });

		// A drag that ends on the photo mustn't also register as the click that
		// advances it (see the image click handler below).
		$row.get(0)?.addEventListener('click', (e) =>
			{
			if (!imageDragged)
				return;
			imageDragged = false;
			e.preventDefault();
			e.stopPropagation();
			}, true);

		// Show image
		const showImage = (index: number) => {
			const direction = index > currentIndex ? 'next' : 'prev';
			const $img = $modal.find('.property-gallery-lightbox__image');
			const $caption = $modal.find('.property-gallery-lightbox__caption');
			
			// Add exit animation
			$img.addClass(`slide-out-${direction}`);
			
			setTimeout(() => {
				currentIndex = index;
				const image = images[index];
				
				$img.removeClass(`slide-out-next slide-out-prev`)
					.addClass(`slide-in-${direction}`)
					.attr('src', image.url)
					.attr('alt', image.caption);
				
				// Gated by the property's own `show_gallery_captions` flag; the
				// line collapses entirely when it's off, so the counter doesn't
				// sit next to an empty gap.
				const caption = showCaptions ? image.caption : '';
				$caption.text(caption).toggleClass('is-empty', !caption);
				$modal.find('.property-gallery-lightbox__counter').text(`${index + 1} / ${images.length}`);

				// Disabled rather than hidden: `toggle()` removed the button
				// from the flow at either end, which shifted the image sideways
				// on the first and last slides.
				$modal.find('.property-gallery-lightbox__prev').prop('disabled', index === 0);
				$modal.find('.property-gallery-lightbox__next').prop('disabled', index === images.length - 1);

				// Highlight the matching thumbnail, and keep it in view -- with
				// 24 images the active one otherwise scrolls off the strip and
				// there's no indication of where you are in the set.
				const $active = $thumbs.find('.property-gallery-lightbox__thumb')
					.removeClass('is-active')
					.eq(index).addClass('is-active');
				$active.get(0)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
				
				// Remove animation class after animation completes
				setTimeout(() => {
					$img.removeClass(`slide-in-next slide-in-prev`);
				}, 300);
			}, 150);
		};

		// The page behind mustn't scroll while the lightbox is up -- a wheel
		// over the backdrop used to run the property page underneath it.
		const scrollLock = $('body').css('overflow');
		$('body').css('overflow', 'hidden');

		const close = () =>
			{
			$('body').css('overflow', scrollLock);
			$modal.fadeOut(200, () => $modal.remove());
			};

		// Event handlers
		$modal.find('.property-gallery-lightbox__close, .property-gallery-lightbox__overlay').on('click', close);

		// Clicking the image advances rather than closes. Closing on the photo
		// itself is the easiest thing in the dialog to hit by accident, and the
		// backdrop, the close button and Escape all still dismiss.
		$modal.find('.property-gallery-lightbox__image').on('click', (e) => {
			e.stopPropagation();
			if (currentIndex < images.length - 1)
				showImage(currentIndex + 1);
			else
				showImage(0);
		});

		$modal.find('.property-gallery-lightbox__prev').on('click', (e) => {
			e.stopPropagation();
			if (currentIndex > 0) {
				showImage(currentIndex - 1);
			}
		});

		$modal.find('.property-gallery-lightbox__next').on('click', (e) => {
			e.stopPropagation();
			if (currentIndex < images.length - 1) {
				showImage(currentIndex + 1);
			}
		});

		// Keyboard navigation
		$(document).on('keydown.lightbox', (e) => {
			if (e.key === 'Escape') {
				$modal.find('.property-gallery-lightbox__close').trigger('click');
			} else if (e.key === 'ArrowLeft') {
				$modal.find('.property-gallery-lightbox__prev').trigger('click');
			} else if (e.key === 'ArrowRight') {
				$modal.find('.property-gallery-lightbox__next').trigger('click');
			}
		});

		// Cleanup keyboard listener on close
		$modal.on('remove', () => {
			$(document).off('keydown.lightbox');
		});

		// Show first image
		showImage(currentIndex);
		$modal.hide().fadeIn(200);
	}
});
